// Ask AI on the System log: what `query_system_log` counts and lists, and what `apply_view` sets on
// the page — its days, person, action and search. Pure: the page (`axai-system-log.ts`) gives the
// entries it reads (`/api/routing/changes`, `changes.ts`) with what their lines say, and its words
// for a time and a change, so an answer reads as the page does.

import type { ChangeAction } from "../entities";
import { CHANGE_ACTIONS } from "../entities";
import type { ChangeView } from "./change-log";
import { fitList } from "./ask-fit";

/** An action as the log's reader says it: "Approved", "Shipped (marked by hand)". */
export const ACTION_WORDS: Record<ChangeAction, string> = {
  edit: "Edited",
  approve: "Approved",
  choose: "Chose a value (2 values)",
  schedule: "Scheduled a pickup",
  "add-line": "Added to a pickup",
  pickup: "Changed a pickup",
  remove: "Taken off a pickup",
  ship: "Shipped (marked by hand)",
  finish: "Finished",
  restore: "Restored",
  list: "Changed a list",
  "pre-route": "Pre-route",
  review: "Reviewed",
  import: "Workbook import",
};

/** Words people use for an action → the action: "approved", "shipped", "restore". */
export function actionOf(word: string): ChangeAction | null {
  const w = word.trim().toLowerCase();
  if (!w) return null;
  if ((CHANGE_ACTIONS as readonly string[]).includes(w)) return w as ChangeAction;
  const stems: [RegExp, ChangeAction][] = [
    [/^approv/, "approve"],
    [/^ship|mark(ed)? shipped/, "ship"],
    [/^schedul/, "schedule"],
    [/^restor/, "restore"],
    [/^(edit|chang|typ|set)/, "edit"],
    [/^(cho|pick(ed)? a value|2 values)/, "choose"],
    [/^(remov|taken off|took off)/, "remove"],
    [/^finish/, "finish"],
    [/^(added|add-line|add a line)/, "add-line"],
    [/^pickup/, "pickup"],
    [/^list/, "list"],
    [/^pre-?rout/, "pre-route"],
    [/^review/, "review"],
    [/^import/, "import"],
  ];
  return stems.find(([re]) => re.test(w))?.[1] ?? null;
}

/** An entry with what its line says now: the page's line, else the entry's own `lineText`. */
export interface LogFacts {
  change: ChangeView;
  /** The entry's day, in the reader's zone (an older entry: its stored day). */
  day: string;
  customers: readonly string[];
  pickTickets: readonly string[];
  /** The line as the log's Line column says it. */
  line: string;
}

export interface LogQuery {
  person?: string[];
  action?: string[];
  /** A line's id, WMS # or text it shows. */
  line?: string;
  pick_ticket?: string;
  customer?: string[];
  from?: string;
  to?: string;
  /** Over the entry's note and changes, as the page's search box. */
  search?: string;
  group_by?: "person" | "action" | "day" | "customer";
  limit?: number;
}

/** The page's words: when an entry was saved, and what it changed. */
export interface LogWords {
  when: (c: ChangeView) => string;
  change: (c: ChangeView) => string;
}

const has = (text: string, part: string) => text.toLowerCase().includes(part.trim().toLowerCase());

/** Whether `person` names the entry's person: a part of the name, or the id (an email). */
export const isPerson = (c: Pick<ChangeView, "personName" | "personId">, person: string) =>
  !!person.trim() && (has(c.personName, person) || c.personId.toLowerCase() === person.trim().toLowerCase());

/** The entries a query keeps, newest first as the page lists them. */
export function logMatches(facts: readonly LogFacts[], q: LogQuery, words: Pick<LogWords, "change">): LogFacts[] {
  const actions = (q.action ?? []).map((a) => actionOf(a) ?? a);
  return facts.filter(
    (f) =>
      (!q.person?.length || q.person.some((p) => isPerson(f.change, p))) &&
      (!actions.length || actions.includes(f.change.action)) &&
      (!q.from || f.day >= q.from) &&
      (!q.to || f.day <= q.to) &&
      (!q.line || f.change.lineId === q.line || has(f.line, q.line)) &&
      (!q.pick_ticket || f.pickTickets.includes(q.pick_ticket.trim()) || has(f.line, q.pick_ticket)) &&
      (!q.customer?.length || q.customer.some((c) => f.customers.some((x) => has(x, c)) || has(f.line, c))) &&
      (!q.search ||
        [f.change.note, words.change(f.change), ...f.change.changes.flatMap((x) => [x.f, x.from, x.to])].some((t) =>
          has(t, q.search!),
        )),
  );
}

/** Counted, grouped and listed for the model: at most 20 entries, each as the page says it, under ~3 KB. */
export function querySystemLog(facts: readonly LogFacts[], q: LogQuery, words: LogWords) {
  const kept = logMatches(facts, q, words);
  const limit = Math.max(0, Math.min(20, q.limit ?? 10));
  const keyOf = (f: LogFacts): string[] =>
    q.group_by === "person"
      ? [f.change.personName || "—"]
      : q.group_by === "action"
        ? [ACTION_WORDS[f.change.action] ?? f.change.action]
        : q.group_by === "day"
          ? [f.day]
          : q.group_by === "customer"
            ? f.customers.length
              ? [...f.customers]
              : ["—"]
            : [];
  const groups = new Map<string, number>();
  for (const f of kept) for (const k of keyOf(f)) groups.set(k, (groups.get(k) ?? 0) + 1);
  const result = {
    matched: kept.length,
    ...(q.group_by
      ? {
          [`by_${q.group_by}`]: Object.fromEntries(
            [...groups].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 15),
          ),
        }
      : {}),
    entries: kept.slice(0, limit).map((f) => ({
      when: words.when(f.change),
      who: f.change.personName,
      action: ACTION_WORDS[f.change.action] ?? f.change.action,
      line: f.line.slice(0, 80),
      what: words.change(f.change).slice(0, 140),
    })),
  };
  return fitList(result, "entries", kept.length);
}

/** The System log's filters Ask AI may set: its days, person, action and search (the Source and My customers stay the reader's). */
export interface LogView {
  preset: "7d" | "30d" | "90d" | "any" | "custom";
  from: string | null;
  to: string | null;
  search: string;
  /** A person's name as the log shows it; "" is everyone. */
  person: string;
  action: ChangeAction | "";
}

export interface LogViewInput {
  person?: string;
  action?: string;
  from?: string;
  to?: string;
  preset?: LogView["preset"];
  search?: string;
  /** Parts to take off: "person", "action", "search", "dates". */
  clear?: string[];
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `apply_view` on the System log: only what the input names changes. A person must be one the log
 * has (the names in `people`), else it is not applied and the names like it are said; an action, one
 * of the log's; dates, whole days (a custom range).
 */
export function applyLogView(
  view: LogView,
  input: LogViewInput,
  people: readonly string[],
): { view: LogView; unapplied: string[] } {
  const next: LogView = { ...view };
  const unapplied: string[] = [];
  for (const c of input.clear ?? []) {
    if (c === "person") next.person = "";
    else if (c === "action") next.action = "";
    else if (c === "search") next.search = "";
    else if (c === "dates") Object.assign(next, { preset: "7d", from: null, to: null });
  }
  if (input.person !== undefined) {
    const p = input.person.trim();
    const exact = people.find((n) => n.toLowerCase() === p.toLowerCase());
    const like = people.filter((n) => has(n, p));
    const found = exact ?? (like.length === 1 ? like[0] : undefined);
    if (!p) next.person = "";
    else if (found) next.person = found;
    else
      unapplied.push(
        like.length
          ? `person "${p}": several people match (${like.slice(0, 5).join(", ")})`
          : `person "${p}": nobody by that name in these days`,
      );
  }
  if (input.action !== undefined) {
    const a = actionOf(input.action);
    if (!input.action.trim()) next.action = "";
    else if (a) next.action = a;
    else unapplied.push(`action "${input.action}"`);
  }
  if (input.preset && input.preset !== "custom")
    Object.assign(next, { preset: input.preset, ...(input.preset === "any" ? { from: null, to: null } : {}) });
  if (input.from !== undefined || input.to !== undefined) {
    const from = input.from && DAY.test(input.from) ? input.from : null;
    const to = input.to && DAY.test(input.to) ? input.to : null;
    if ((input.from && !from) || (input.to && !to)) unapplied.push("dates: YYYY-MM-DD");
    else Object.assign(next, { preset: "custom", from: from ?? to, to: to ?? from });
  }
  if (input.search !== undefined) next.search = input.search.trim();
  return { view: next, unapplied };
}

/** The view as one key: Undo shows while the page is still the view Ask AI set. */
export const logViewKey = (v: LogView) => JSON.stringify([v.preset, v.from, v.to, v.search, v.person, v.action]);

/** The view in words, for the prompt and the answer. */
export function logViewWords(v: LogView): string {
  const days = v.preset === "any" ? "any date" : v.from ? `${v.from} to ${v.to}` : `the last ${v.preset}`;
  return [
    days,
    v.person ? `person ${v.person}` : "",
    v.action ? `action ${ACTION_WORDS[v.action]}` : "",
    v.search ? `search "${v.search}"` : "",
  ]
    .filter(Boolean)
    .join(", ");
}
